import React from 'react';
import {observer} from 'mobx-react';
import {Tag} from '@carbon/react';
import {CheckmarkFilled, ErrorFilled, Renew} from '@carbon/icons-react';
import {
    ProcessingFile,
    StageStatus,
    StageGroup,
    STAGE_GROUPS_ORDER,
    getStagesByGroup
} from '../../../lib/types';
import {StageItemProps} from './types';

const StageIcon: React.FC<{status: StageStatus}> = ({status}) => {
    if (status === 'complete') {
        return <CheckmarkFilled size={20} className="stage-icon stage-complete" />;
    }
    if (status === 'failed') {
        return <ErrorFilled size={20} className="stage-icon stage-failed" />;
    }
    if (status === 'processing') {
        return <Renew size={20} className="stage-icon stage-processing rotating" />;
    }
    return <div className="stage-icon stage-pending" />;
};

const StageItem: React.FC<StageItemProps> = ({stageName, status, isFailed, additionalInfo}) => {
    const getStatusTag = () => {
        if (status === 'processing') {
            return <Tag type="blue" size="sm" className="stage-tag">Processing</Tag>;
        }
        if (status === 'complete') {
            return <Tag type="green" size="sm" className="stage-tag">Complete</Tag>;
        }
        if (status === 'failed') {
            return <Tag type="red" size="sm" className="stage-tag">Failed</Tag>;
        }
        if (status === 'pending') {
            return <Tag type="gray" size="sm" className="stage-tag">Pending</Tag>;
        }
        return null;
    };

    return (
        <div className={`stage-item ${isFailed ? 'stage-error' : ''}`}>
            <StageIcon status={status} />
            <div className="stage-content">
                <div className="stage-header">
                    <span className="stage-name">{stageName}</span>
                    {getStatusTag()}
                </div>
                {additionalInfo && (
                    <p className="stage-timestamp">{additionalInfo}</p>
                )}
            </div>
        </div>
    );
};

interface StagesListProps {
    file: ProcessingFile;
}

/**
 * Get display name for stage group
 */
const getStageGroupDisplayName = (group: StageGroup): string => {
    const displayNames: Record<StageGroup, string> = {
        'validation': 'Validation',
        'roster_ingestion': 'Roster Ingestion',
        'geocoding': 'Geocoding',
        'roster_setup': 'Roster Setup',
        'compliance_reporting': 'Compliance Reporting'
    };
    return displayNames[group];
};

/**
 * Calculate the overall status of a stage group based on its stages
 */
const calculateGroupStatus = (group: StageGroup, file: ProcessingFile): StageStatus => {
    const stages = getStagesByGroup(group);
    const statuses = stages.map(stageKey => file.stages[stageKey]);

    // If any stage failed, group is failed
    if (statuses.includes('failed')) {
        return 'failed';
    }

    // If any stage is processing, group is processing
    if (statuses.includes('processing')) {
        return 'processing';
    }

    // If all stages are complete, group is complete
    if (statuses.every(status => status === 'complete')) {
        return 'complete';
    }

    return 'pending';
};

const StagesList: React.FC<StagesListProps> = observer(({file}) => {
    const stageGroupConfig = STAGE_GROUPS_ORDER.map((group) => {
        const status = calculateGroupStatus(group, file);

        return {
            name: getStageGroupDisplayName(group),
            status
        };
    });

    return (
        <div className="stages-section">
            <h4>Processing Stages</h4>
            <div className="stages-list">
                {stageGroupConfig.map((stageGroup, idx) => (
                    <StageItem
                        key={idx}
                        stageName={stageGroup.name}
                        status={stageGroup.status}
                        isFailed={stageGroup.status === 'failed'}
                    />
                ))}
            </div>
        </div>
    );
});

export default StagesList;
